import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { CqmAmcDashboardClient, CqmAmcRecomputeRequestDto, MipsPiMeasuresClient } from '@medone/medonehp-api-client';
import moment from 'moment';

import { Axios } from '../../../shared/common/http';
import { RootState } from '../../../shared/store';
import { AmcState, initialAmcState } from './amc-models';

const buildClient = () => new CqmAmcDashboardClient(null as any, Axios);
const buildPiClient = () => new MipsPiMeasuresClient(null as any, Axios);

export const fetchAmcInventory = createAsyncThunk('amc/fetchInventory', async () => {
    const response = await buildClient().getInventory();
    if (response.result.succeeded && response.result.entity) {
        return response.result.entity;
    }
    throw new Error(response.result.message || 'Failed to load measure inventory.');
});

export const fetchAmcDashboard = createAsyncThunk('amc/fetchDashboard', async ({ year, importRunId }: { year: number; importRunId?: number | null }) => {
    const response = await buildClient().getDashboard(year, importRunId ?? undefined);
    if (response.result.succeeded && response.result.entity) {
        return response.result.entity;
    }
    throw new Error(response.result.message || 'Failed to load the AMC dashboard.');
});

export const fetchMipsPiDashboard = createAsyncThunk('amc/fetchMipsPi', async ({ periodStart, periodEnd }: { periodStart: string; periodEnd: string }) => {
    const response = await buildPiClient().dashboard(moment(periodStart), moment(periodEnd));
    if (response.result.succeeded && response.result.entity) {
        return response.result.entity;
    }
    throw new Error(response.result.message || 'Failed to load the Promoting Interoperability measures.');
});

// rejectWithValue (not the default serialized action.error) so the 409-in-flight status survives
// to the component — the generated client throws ApiException (status + parsed ProblemDetails).
export const triggerAmcRecompute = createAsyncThunk('amc/triggerRecompute', async ({ trigger, year }: { trigger: string; year: number }, { rejectWithValue }) => {
    try {
        const response = await buildClient().recompute(CqmAmcRecomputeRequestDto.fromJS({ trigger, year }));
        if (response.result.succeeded && response.result.entity) {
            return response.result.entity;
        }
        return rejectWithValue({ status: null, message: response.result.message || 'Failed to trigger the recompute.' });
    } catch (err: any) {
        return rejectWithValue({ status: err?.status ?? null, message: err?.result?.detail ?? err?.message ?? 'Failed to trigger the recompute.' });
    }
});

const slice = createSlice({
    name: 'amc',
    initialState: initialAmcState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAmcInventory.pending, (state: AmcState) => {
                state.inventoryLoading = true;
                state.errorMessage = null;
            })
            .addCase(fetchAmcInventory.fulfilled, (state: AmcState, action) => {
                state.inventoryLoading = false;
                state.inventory = action.payload;
            })
            .addCase(fetchAmcInventory.rejected, (state: AmcState, action) => {
                state.inventoryLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load measure inventory.';
            })
            .addCase(fetchAmcDashboard.pending, (state: AmcState) => {
                state.dashboardLoading = true;
                state.errorMessage = null;
            })
            .addCase(fetchAmcDashboard.fulfilled, (state: AmcState, action) => {
                state.dashboardLoading = false;
                state.dashboard = action.payload;
            })
            .addCase(fetchAmcDashboard.rejected, (state: AmcState, action) => {
                state.dashboardLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the AMC dashboard.';
            })
            .addCase(fetchMipsPiDashboard.pending, (state: AmcState) => {
                state.piLoading = true;
                state.errorMessage = null;
            })
            .addCase(fetchMipsPiDashboard.fulfilled, (state: AmcState, action) => {
                state.piLoading = false;
                state.pi = action.payload;
            })
            .addCase(fetchMipsPiDashboard.rejected, (state: AmcState, action) => {
                state.piLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the Promoting Interoperability measures.';
            })
            .addCase(triggerAmcRecompute.pending, (state: AmcState) => {
                state.recomputing = true;
                state.errorMessage = null;
            })
            .addCase(triggerAmcRecompute.fulfilled, (state: AmcState) => {
                state.recomputing = false;
            })
            .addCase(triggerAmcRecompute.rejected, (state: AmcState, action) => {
                state.recomputing = false;
                state.errorMessage = (action.payload as { message?: string } | undefined)?.message ?? action.error.message ?? 'Failed to trigger the recompute.';
            });
    },
});

export const selectAmcInventory = (state: RootState) => (state as any).amc?.inventory ?? null;
export const selectAmcDashboard = (state: RootState) => (state as any).amc?.dashboard ?? null;
export const selectAmcInventoryLoading = (state: RootState) => (state as any).amc?.inventoryLoading ?? false;
export const selectAmcDashboardLoading = (state: RootState) => (state as any).amc?.dashboardLoading ?? false;
export const selectAmcRecomputing = (state: RootState) => (state as any).amc?.recomputing ?? false;
export const selectMipsPi = (state: RootState) => (state as any).amc?.pi ?? null;
export const selectMipsPiLoading = (state: RootState) => (state as any).amc?.piLoading ?? false;
export const selectAmcError = (state: RootState) => (state as any).amc?.errorMessage ?? null;

export default slice.reducer;
